import React, { useMemo } from 'react';
import { format, isSameDay } from 'date-fns';
import { weekDays, layoutWeekBars, dayKey, parseTime, spansDay, fmtMinutes } from './calendarUtils';
import { EventBar } from './EventBar';
import { EventHoverCard } from './EventHoverCard';

const HOUR_PX = 48;

// Side-by-side columns for overlapping timed blocks within one day
const packDay = (blocks) => {
  const sorted = [...blocks].sort((a, b) => a.s - b.s);
  let cluster = [];
  let colEnds = [];
  let clusterEnd = -1;
  const close = () => cluster.forEach((b) => { b.cols = colEnds.length; });
  sorted.forEach((b) => {
    if (b.s >= clusterEnd) { close(); cluster = []; colEnds = []; }
    let c = colEnds.findIndex((end) => end <= b.s);
    if (c === -1) { c = colEnds.length; colEnds.push(b.e); } else colEnds[c] = b.e;
    b.col = c;
    cluster.push(b);
    clusterEnd = Math.max(clusterEnd, b.e);
  });
  close();
  return sorted;
};

const TimedBlock = ({ b, startHour, testId }) => (
  <EventHoverCard event={b.event}>
    <button type="button" onClick={() => b.event.onClick && b.event.onClick()}
      className={`absolute flex flex-col justify-start rounded-md px-1.5 py-1 text-left text-[10px] leading-tight text-white overflow-hidden hover:brightness-110 transition-[filter] shadow-sm ${b.event.muted ? 'opacity-50 line-through' : ''}`}
      style={{ top: ((b.s - startHour * 60) / 60) * HOUR_PX, height: Math.max(((b.e - b.s) / 60) * HOUR_PX - 2, 18),
        left: `calc(${(b.col / b.cols) * 100}% + 2px)`, width: `calc(${100 / b.cols}% - 4px)`, background: b.event.color }}
      data-testid={`${testId}-timed-${b.event.id}-${b.day}`}>
      <span className="font-semibold block truncate">{b.event.title}</span>
      {b.cols < 3 && <span className="opacity-90 block truncate">{fmtMinutes(b.s)} – {fmtMinutes(b.e)}</span>}
    </button>
  </EventHoverCard>
);

export const WeekView = ({ cursor, events, testId }) => {
  const days = useMemo(() => weekDays(cursor), [cursor]);
  const allDay = events.filter((e) => parseTime(e.startTime) == null);
  const timed = events.filter((e) => parseTime(e.startTime) != null);
  const bars = useMemo(() => layoutWeekBars(allDay, days), [allDay, days]);
  const blocksByDay = days.map((d) => packDay(timed.filter((e) => spansDay(e.start, e.end, d)).map((e) => {
    const s = parseTime(e.startTime);
    const en = parseTime(e.endTime);
    return { event: e, s, e: en != null && en > s ? en : s + 60, day: dayKey(d) };
  })));
  const flat = blocksByDay.flat();
  const startHour = Math.min(8, ...flat.map((b) => Math.floor(b.s / 60)));
  const endHour = Math.max(19, ...flat.map((b) => Math.ceil(b.e / 60)));
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`${testId}-week`}>
      <div className="grid border-b bg-muted/40" style={{ gridTemplateColumns: '64px repeat(7, 1fr)' }}>
        <div />
        {days.map((d) => (
          <div key={dayKey(d)} className="py-2 text-center border-l">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{format(d, 'EEE')}</div>
            <div className={`mx-auto mt-0.5 w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold ${isSameDay(d, now) ? 'bg-lime text-lime-foreground' : ''}`}>{format(d, 'd')}</div>
          </div>
        ))}
      </div>
      <div className="grid border-b" style={{ gridTemplateColumns: '64px 1fr' }}>
        <div className="text-[10px] text-muted-foreground px-2 py-1.5">All day</div>
        <div className="grid grid-cols-7 gap-y-[3px] py-1 border-l min-h-[26px]" style={{ gridAutoRows: '18px' }} data-testid={`${testId}-allday`}>
          {bars.map((b) => <EventBar key={b.event.id} bar={b} testId={testId} />)}
        </div>
      </div>
      <div className="overflow-y-auto max-h-[620px]">
        <div className="grid relative" style={{ gridTemplateColumns: '64px repeat(7, 1fr)' }}>
          <div>{hours.map((h) => <div key={h} className="text-[10px] text-muted-foreground text-right pr-2 -mt-1.5" style={{ height: HOUR_PX }}>{h > startHour ? fmtMinutes(h * 60).replace(':00', '') : ''}</div>)}</div>
          {days.map((d, i) => (
            <div key={dayKey(d)} className={`relative border-l ${isSameDay(d, now) ? 'bg-lime/5' : ''}`} style={{ height: hours.length * HOUR_PX }} data-testid={`${testId}-week-day-${dayKey(d)}`}>
              {hours.map((h) => <div key={h} className="border-b border-dashed border-border/60" style={{ height: HOUR_PX }} />)}
              {isSameDay(d, now) && nowMin >= startHour * 60 && nowMin <= endHour * 60 && (
                <div className="absolute inset-x-0 h-0.5 bg-red-500 z-10" style={{ top: ((nowMin - startHour * 60) / 60) * HOUR_PX }} />
              )}
              {blocksByDay[i].map((b) => <TimedBlock key={b.event.id} b={b} startHour={startHour} testId={testId} />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
